/**
 * Maps a `result` event's tool name to the data plane's own card component --
 * nothing here redraws a chart, every one of the eight is the exact
 * component `fe-data-plane` already ships and reviews. `.ts`, not `.tsx`:
 * `React.createElement` throughout, per this folder's own convention.
 *
 * `result.payload` is already validated against the named tool's schema by
 * `stream.ts`'s `parseTurnEvents` before an event ever reaches here (the
 * whole reason `RESULT_PAYLOAD_SCHEMAS` exists), so the cast per case below
 * trusts that upstream contract rather than re-parsing it.
 */

import { createElement, Fragment, type ReactNode } from 'react';

import { Withheld } from '../../../chrome/measure';
import { DimensionBars, type DimensionBarRow } from '../../../chrome/chart/DimensionBars';
import { EmptyState } from '../../../chrome/state';
import type {
  Amplification,
  Comments,
  CommentTerms,
  Composition,
  PostMetrics,
  Posts,
  Sentiment,
  Territory,
} from '../api/schemas';
import { AmplifiersBody } from '../publication/AmplifiersBody';
import { CommentsBody } from '../publication/CommentsBody';
import { ProviderMetricsGrid } from '../publication/MetricsGrid';
import { PostCard } from '../publication/PostCard';
import { TermsBody } from '../publication/TermsBody';
import { NcsSeries } from '../sentiment/NcsSeries';
import { sentimentPath } from '../sentiment/paths';
import { SentimentCard } from '../sentiment/SentimentCard';
import { labelValue } from '../publication/compute';
import { byLevel } from '../sentiment/series';
import type { ProvenanceStripProps } from '../shared/ProvenanceStrip';
import { CompositionCard as TerritoryCard } from '../territory/CompositionCard';
import { ChartCard, QueryCard } from './ResultCards';
import { at } from './messages';
import { isToolName, type ChartResult, type QueryResult, type ResultData } from './schemas';

/** What a card needs to draw its provenance strip -- the same campaign and
 *  window scope `Assistant.tsx`'s composer already shows, since every card
 *  in a turn was answered against that one scope. */
export interface AssistantCardContext {
  campaignId: string;
  windowLabel: string;
}

/** Every card this dispatcher builds through `SentimentCard` deep-links to
 *  the sentiment screen: each of the six tool payloads below is the same
 *  figure that screen already renders (`DimensionCard`, `TermsCard`,
 *  `TagsCard`, `PostScoresCard`, `RegionCard`), so it is the figure's own
 *  screen regardless of which one post or account a row names. `measured`
 *  is not toggleable yet -- none of these six payloads carry the estimate
 *  pipeline's fidelity fields the territory screen's own card reads via
 *  `labellingOf`, so every assistant read is reported as measured. */
function provenanceFor(
  context: AssistantCardContext | undefined,
): ProvenanceStripProps | undefined {
  if (context === undefined) return undefined;
  return {
    measured: true,
    windowLabel: context.windowLabel,
    to: sentimentPath(context.campaignId),
    label: at('provenance.openInSentiment'),
  };
}

function metricsCard(payload: PostMetrics, context: AssistantCardContext | undefined): ReactNode {
  return createElement(SentimentCard, {
    title: at('card.post_metrics.title'),
    provenance: provenanceFor(context),
    children: createElement(ProviderMetricsGrid, { rows: payload.rows }),
  });
}

function amplificationCard(
  payload: Amplification,
  context: AssistantCardContext | undefined,
): ReactNode {
  return createElement(SentimentCard, {
    title: at('card.amplification.title'),
    provenance: provenanceFor(context),
    children: createElement(AmplifiersBody, {
      rows: payload.rows,
      campaignId: context?.campaignId,
    }),
  });
}

function commentTermsCard(
  payload: CommentTerms,
  context: AssistantCardContext | undefined,
): ReactNode {
  return createElement(SentimentCard, {
    title: at('card.comment_terms.title'),
    provenance: provenanceFor(context),
    children: createElement(TermsBody, { rows: payload.rows }),
  });
}

function commentsCard(payload: Comments, context: AssistantCardContext | undefined): ReactNode {
  return createElement(SentimentCard, {
    title: at('card.comments.title'),
    provenance: provenanceFor(context),
    children: createElement(CommentsBody, {
      rows: payload.rows,
      counts: payload.counts,
      hasMore: false,
      loadingMore: false,
      onMore: () => {},
    }),
  });
}

function postsCard(payload: Posts): ReactNode {
  if (payload.rows.length === 0) {
    return createElement(EmptyState, { title: at('card.posts.empty') });
  }
  return createElement(
    'div',
    { className: 'flex flex-col gap-3' },
    ...payload.rows.map((post) => createElement(PostCard, { key: post.post_id, post })),
  );
}

/** ASSUMED deviation: the dashboard's own composition reading
 *  (`publication/compute.ts`'s `readComposition`) narrows each cell against a
 *  separate comments-population basis a single tool call cannot supply, so
 *  this renders the gated cells directly on `DimensionBars` -- the same
 *  primitive the dashboard's signed-lean dimensions use, at a `[0, max]`
 *  domain so every bar reads as a plain left-anchored count rather than a
 *  lean around zero. */
function compositionCard(
  payload: Composition,
  context: AssistantCardContext | undefined,
): ReactNode {
  // One bar per attribute group, its stances summed: every summed cell already
  // cleared the anonymity threshold on its own, so the sum discloses nothing new.
  const byGroup = new Map<string, number>();
  for (const cell of payload.cells) {
    const entries = Object.entries(cell.attributes);
    const label =
      entries.length === 0
        ? at('card.composition.unidentified')
        : entries.map(([dimension, value]) => labelValue(dimension, value)).join(' · ');
    const prior = byGroup.get(label);
    byGroup.set(label, prior === undefined ? cell.count : prior + cell.count);
  }
  const groups = [...byGroup].sort((a, b) => b[1] - a[1]);
  const rows: DimensionBarRow[] = groups.map(([label, value]) => ({ label, value, n: null }));
  const maxCount = Math.max(1, ...groups.map(([, value]) => value));
  return createElement(SentimentCard, {
    title: at('card.composition.title'),
    provenance: provenanceFor(context),
    children: createElement(
      Fragment,
      null,
      createElement(Withheld, {
        suppressedShare: payload.suppressed_share,
        total: payload.total,
        source: 'commenters',
      }),
      rows.length === 0
        ? null
        : createElement(DimensionBars, {
            title: at('card.composition.title'),
            domain: [0, maxCount],
            rows,
          }),
    ),
  });
}

function sentimentCard(payload: Sentiment, context: AssistantCardContext | undefined): ReactNode {
  const levels = byLevel(payload.points);
  return createElement(
    Fragment,
    null,
    ...levels.map((series) =>
      createElement(SentimentCard, {
        key: series.level ?? 'overall',
        title:
          series.level === null
            ? at('card.sentiment.title')
            : at('card.sentiment.titleByLevel', { level: series.level }),
        provenance: provenanceFor(context),
        children: createElement(NcsSeries, { series }),
      }),
    ),
  );
}

function territoryCard(payload: Territory): ReactNode {
  return createElement(TerritoryCard, { territory: payload });
}

/** `null` when the tool errored, returned no payload, or is a name this
 *  client does not declare -- the screen owns whatever it shows alongside a
 *  `null` card, which is outside this dispatcher's own job.
 *
 *  `context` is omitted by a caller with no scope to hand over (this file's
 *  own tests): the card still renders, just without the provenance strip.
 *  `get_territory` and `get_posts` never carry one either way, since neither
 *  goes through `SentimentCard`. */
/** The tools whose card is a chart: the answer itself, drawn inline, never folded
 *  away with the rows that back it. */
const CHART_TOOLS: ReadonlySet<string> = new Set([
  'get_composition',
  'get_campaign_composition',
  'get_sentiment_series',
  'render_chart',
]);

export function isChartResult(name: string): boolean {
  return CHART_TOOLS.has(name);
}

function day(iso: string): string {
  const [, month, dayOfMonth] = iso.slice(0, 10).split('-');
  return `${dayOfMonth}/${month}`;
}

/** The half-open window as the days it covers, read off the dates the model wrote in the
 *  campaign's own offset: an end at midnight closes the day before it. */
export function windowLabelOf(window: { from: string; to: string }): string {
  const midnight = /T00:00(:00(\.0+)?)?/.test(window.to);
  const last = midnight
    ? new Date(Date.parse(`${window.to.slice(0, 10)}T00:00:00Z`) - 86_400_000)
        .toISOString()
        .slice(0, 10)
    : window.to.slice(0, 10);
  const first = window.from.slice(0, 10);
  return first >= last ? day(first) : `${day(first)} – ${day(last)}`;
}

/** The screen's scope, with the window narrowed to the one the tool actually read. */
export function cardContextFor(
  result: ResultData,
  scope: AssistantCardContext | undefined,
): AssistantCardContext | undefined {
  return scope !== undefined && result.window != null
    ? { ...scope, windowLabel: windowLabelOf(result.window) }
    : scope;
}

export function cardFor(result: ResultData, scope?: AssistantCardContext): ReactNode {
  if (result.error !== null || result.payload === null || !isToolName(result.name)) return null;
  const context = cardContextFor(result, scope);
  switch (result.name) {
    case 'get_post_metrics':
      return metricsCard(result.payload as PostMetrics, context);
    case 'get_amplification':
      return amplificationCard(result.payload as Amplification, context);
    case 'get_posts':
      return postsCard(result.payload as Posts);
    case 'get_composition':
    case 'get_campaign_composition':
      return compositionCard(result.payload as Composition, context);
    case 'get_comment_terms':
      return commentTermsCard(result.payload as CommentTerms, context);
    case 'get_comments':
      return commentsCard(result.payload as Comments, context);
    case 'get_sentiment_series':
      return sentimentCard(result.payload as Sentiment, context);
    case 'get_territory':
      return territoryCard(result.payload as Territory);
    case 'run_query':
    case 'get_counts':
      return createElement(QueryCard, { result: result.payload as QueryResult });
    case 'render_chart':
      // No provenance strip: the window is whatever the query's own SQL read, not the screen's.
      return createElement(ChartCard, { result: result.payload as ChartResult });
  }
}
